import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Popover, Empty, Spin } from 'antd';
import { BellOutlined, BellFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';
import { useUnreadCount, useNotifications, useMarkRead, useMarkAllRead } from '../hooks/api/notifications/notificationBell-hooks';

dayjs.extend(relativeTime);

function truncate(str: string | null | undefined, max: number): string {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

// Short two-tone "ping" synthesized via Web Audio API — no audio file needed,
// works everywhere, no asset to fetch/host.
function playNotificationPing() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const tone = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.15, now + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + duration);
    };

    tone(880, 0, 0.14);
    tone(1320, 0.09, 0.16);

    // Free the AudioContext once the tones have finished playing.
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch {
    // Autoplay/audio can be blocked by the browser (e.g. no prior user
    // interaction) — never let a sound failure disrupt notifications.
  }
}

// Maps a notification's entity to where it should take the user.
// Job/Quote deep-link to the exact record (both pages already support opening
// a specific item via a query param); other types land on their list screen
// until those pages grow the same deep-link support.
function resolveNotificationRoute(entityType: string | null | undefined, entityId: number | string | null | undefined): string | null {
  if (!entityType) return null;
  switch (entityType) {
    case 'job':
      return entityId ? `/jobs?job_id=${entityId}` : '/jobs';
    case 'quote':
      return entityId ? `/quotes?quote=${entityId}` : '/quotes';
    case 'invoice':
      return '/invoices';
    case 'timesheet':
      return '/timesheets';
    case 'client':
      return '/clients';
    default:
      return null;
  }
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const prevCountRef = useRef<number | null>(null);

  const { data: countData } = useUnreadCount();
  const { data: listData, isLoading } = useNotifications({ page: 1 });
  const { mutate: markRead } = useMarkRead();
  const { mutate: markAllRead, isPending: markingAll } = useMarkAllRead();

  const count = countData?.count ?? 0;
  const notifications: any[] = listData?.data ?? [];

  // Ping only when the unread count genuinely rises (a new notification
  // arrived via polling) — not on first load, and not when it goes down
  // from marking things read.
  useEffect(() => {
    if (prevCountRef.current !== null && count > prevCountRef.current) {
      playNotificationPing();
    }
    prevCountRef.current = count;
  }, [count]);

  const handleNotificationClick = (n: any) => {
    if (!n.is_read) markRead(n.id);
    const target = resolveNotificationRoute(n.entity_type, n.entity_id);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  const content = (
    <div style={{ width: 360 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-2 pb-2 border-b border-slate-100">
        <span className="text-sm font-semibold text-slate-700">Notifications</span>
        {count > 0 && (
          <Button
            size="small"
            type="link"
            loading={markingAll}
            onClick={() => markAllRead()}
            className="!text-xs !p-0 !h-auto text-blue-600"
          >
            Mark all read
          </Button>
        )}
      </div>

      {/* List */}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spin size="small" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            description="No notifications"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="py-8"
          />
        ) : (
          notifications.map((n: any) => (
            <div
              key={n.id}
              onClick={() => handleNotificationClick(n)}
              className={`flex gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-l-2 ${
                n.is_read ? 'border-transparent' : 'border-blue-500 bg-blue-50/40'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm leading-tight mb-0.5 ${
                    n.is_read ? 'text-slate-600 font-normal' : 'text-slate-800 font-semibold'
                  }`}
                >
                  {n.title}
                </p>
                {n.body && (
                  <p className="text-xs text-slate-500 leading-snug mb-0.5">
                    {truncate(n.body, 80)}
                  </p>
                )}
                <p className="text-[11px] text-slate-400">
                  {dayjs(n.created_at).fromNow()}
                </p>
              </div>
              {!n.is_read && (
                <div className="mt-1 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      arrow={false}
      overlayStyle={{ padding: 0 }}
      overlayInnerStyle={{ padding: 0, borderRadius: 10, overflow: 'hidden' }}
    >
      <div className="relative cursor-pointer">
        <Badge
          count={count}
          overflowCount={99}
          size="small"
          offset={[-2, 2]}
        >
          {count > 0 ? (
            <BellFilled className="text-xl text-primary" />
          ) : (
            <BellOutlined className="text-xl text-slate-500 hover:text-primary transition-colors" />
          )}
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationBell;
